import React, { memo, useMemo } from 'react';

const FunnelBar = memo(({ label, value, pct, color }) => (
  <div>
    <div className="flex justify-between text-xs mb-1">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold text-gray-700">
        {value.toLocaleString()} <span className="text-gray-400 font-normal">({pct.toFixed(0)}%)</span>
      </span>
    </div>
    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
    </div>
  </div>
));

const QuickStats = memo(({ stats }) => {
  const d = useMemo(() => {
    const total = stats.delivered + stats.running + stats.cancelled || 1;
    const upiTotal = stats.upi + stats.cod || 1;
    const upiPct = Math.round((stats.upi / upiTotal) * 100);
    const successRate = ((stats.delivered / total) * 100).toFixed(1);
    const cancelRate = ((stats.cancelled / total) * 100).toFixed(1);
    return {
      total,
      successRate,
      cancelRate,
      upiPct,
      codPct: 100 - upiPct,
      avgRevenue: Math.round(stats.totalRevenue / total || 0),
      revenuePerUser: Math.round(stats.totalRevenue / (stats.totalUsers || 1)),
      ordersPerUser: (total / (stats.totalUsers || 1)).toFixed(2),
      health: parseFloat(successRate) >= 80
        ? { label: 'Healthy', color: 'text-emerald-600', bg: 'bg-emerald-50', dot: 'bg-emerald-500' }
        : parseFloat(successRate) >= 60
        ? { label: 'Average', color: 'text-amber-600', bg: 'bg-amber-50', dot: 'bg-amber-500' }
        : { label: 'At Risk', color: 'text-rose-600', bg: 'bg-rose-50', dot: 'bg-rose-500' },
    };
  }, [stats]);

  const metricGrid = useMemo(() => [
    { label: 'Avg/Order', value: `₹${d.avgRevenue.toLocaleString()}`, color: 'text-indigo-600' },
    { label: 'Rev/User', value: `₹${d.revenuePerUser.toLocaleString()}`, color: 'text-violet-600' },
    { label: 'Orders/User', value: d.ordersPerUser, color: 'text-sky-600' },
    { label: 'Cancel Rate', value: `${d.cancelRate}%`, color: parseFloat(d.cancelRate) > 15 ? 'text-rose-600' : 'text-gray-600' },
  ], [d]);

  return (
    <div className="space-y-3 h-full">

      {/* Health */}
      <div className={`${d.health.bg} rounded-xl p-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${d.health.dot} animate-pulse`} />
          <span className="text-xs font-semibold text-gray-600">Business Health</span>
        </div>
        <div className="text-right">
          <span className={`text-sm font-bold ${d.health.color}`}>{d.health.label}</span>
          <span className="text-xs text-gray-400 ml-1">· {d.successRate}%</span>
        </div>
      </div>

      {/* Funnel */}
      <div className="space-y-2">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Order Funnel</p>
        <FunnelBar label="Delivered" value={stats.delivered} pct={stats.delivered / d.total * 100} color="bg-emerald-500" />
        <FunnelBar label="Running" value={stats.running} pct={stats.running / d.total * 100} color="bg-amber-400" />
        <FunnelBar label="Cancelled" value={stats.cancelled} pct={stats.cancelled / d.total * 100} color="bg-rose-400" />
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 gap-2">
        {metricGrid.map((item, i) => (
          <div key={i} className="bg-gray-50 rounded-lg p-2.5">
            <p className="text-xs text-gray-400">{item.label}</p>
            <p className={`text-sm font-bold ${item.color} mt-0.5`}>{item.value}</p>
          </div>
        ))}
      </div>

      {/* Payment split */}
      <div className="bg-gray-50 rounded-xl p-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Payment Split</p>
        <div className="flex h-2 rounded-full overflow-hidden bg-gray-200">
          <div className="bg-blue-500 h-full" style={{ width: `${d.upiPct}%` }} />
          <div className="bg-purple-400 h-full flex-1" />
        </div>
        <div className="flex justify-between text-xs mt-1.5">
          <span className="text-blue-600 font-medium">UPI {d.upiPct}% · {stats.upi}</span>
          <span className="text-purple-600 font-medium">COD {d.codPct}% · {stats.cod}</span>
        </div>
      </div>

      {/* Alert */}
      {parseFloat(d.cancelRate) > 15 && (
        <div className="flex items-start gap-2 p-2.5 bg-rose-50 border border-rose-100 rounded-xl">
          <span className="text-rose-500 text-sm flex-shrink-0">⚠</span>
          <div>
            <p className="text-xs font-semibold text-rose-700">High Cancellation</p>
            <p className="text-xs text-rose-400">{d.cancelRate}% — review pickup slots</p>
          </div>
        </div>
      )}
    </div>
  );
});

export default QuickStats;
